import { NavLink, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context-value';
import type { PermissionCode } from '../../types';
import {
  ActivityTypesSection,
  ListsSection,
  LossReasonsSection,
  ProductsSection,
  TagsSection,
} from './CatalogSections';
import { ApiKeysSection } from './ApiKeysSection';
import { AppearanceSection } from './AppearanceSection';
import { AutomationExecutionsSection } from './AutomationExecutionsSection';
import { CompanySection } from './CompanySection';
import { CustomFieldsSection } from './CustomFieldsSection';
import { JobsSection } from './JobsSection';
import { MembersSection } from './MembersSection';
import { NotificationSettingsSection } from './NotificationSettingsSection';
import {
  ConnectionsSection,
  DepartmentsSection,
  WorkingHoursSection,
} from './MultiserviceSections';
import { ProfileSection } from './ProfileSection';
import { SystemParametersSection } from './SystemParametersSection';
import { TrashSection } from './TrashSection';

interface SectionDef {
  key: string;
  label: string;
  adminOnly?: boolean;
  /** Também aparece para quem tem um destes papéis (o admin vê sempre). */
  permission?: PermissionCode | PermissionCode[];
  render: () => React.ReactNode;
}

/**
 * Configurações (/config/* do DataCrazy), na ordem do menu dele. O que ainda não
 * existe aqui fica de fora do menu: Planos, Integrações, Chaves de API, MCP,
 * Armazenamento, Notificações e Parâmetros do sistema.
 */
const GROUPS: Array<{ title: string; sections: SectionDef[] }> = [
  {
    title: 'Conta',
    sections: [
      { key: 'meu-perfil', label: 'Meu perfil', render: () => <ProfileSection /> },
      {
        key: 'notificacoes',
        label: 'Notificações',
        render: () => <NotificationSettingsSection />,
      },
      { key: 'empresa', label: 'Empresa', adminOnly: true, render: () => <CompanySection /> },
      {
        key: 'aparencia',
        label: 'Aparência',
        adminOnly: true,
        render: () => <AppearanceSection />,
      },
      { key: 'membros', label: 'Membros', adminOnly: true, render: () => <MembersSection /> },
    ],
  },
  {
    title: 'Cadastros',
    sections: [
      { key: 'tags', label: 'Tags', render: () => <TagsSection /> },
      { key: 'produtos', label: 'Produtos', render: () => <ProductsSection /> },
      {
        key: 'motivos-de-perda',
        label: 'Motivos de perda',
        render: () => <LossReasonsSection />,
      },
      { key: 'listas', label: 'Listas', render: () => <ListsSection /> },
      {
        key: 'campos-adicionais',
        label: 'Campos adicionais',
        render: () => <CustomFieldsSection />,
      },
      {
        key: 'tipos-de-atividade',
        label: 'Tipos de atividade',
        render: () => <ActivityTypesSection />,
      },
    ],
  },
  {
    title: 'Multiatendimento',
    sections: [
      {
        key: 'departamentos',
        label: 'Departamentos',
        permission: 'multiservice_supervisor',
        render: () => <DepartmentsSection />,
      },
      {
        key: 'horarios',
        label: 'Horários de trabalho',
        permission: 'multiservice_supervisor',
        render: () => <WorkingHoursSection />,
      },
      {
        key: 'conexoes',
        label: 'Conexões',
        permission: 'multiservice_supervisor',
        render: () => <ConnectionsSection />,
      },
    ],
  },
  {
    title: 'Sistema',
    sections: [
      {
        key: 'chaves-de-api',
        label: 'Chaves de API e MCP',
        adminOnly: true,
        render: () => <ApiKeysSection />,
      },
      { key: 'execucoes', label: 'Execuções', render: () => <JobsSection /> },
      {
        key: 'execucoes-automacao',
        label: 'Execuções de automação',
        permission: ['flow_admin', 'flow_member'],
        render: () => <AutomationExecutionsSection />,
      },
      { key: 'lixeira', label: 'Lixeira', adminOnly: true, render: () => <TrashSection /> },
      {
        key: 'parametros',
        label: 'Parâmetros do sistema',
        permission: ['flow_admin', 'flow_member'],
        render: () => <SystemParametersSection />,
      },
    ],
  },
];

export function SettingsPage() {
  const { section } = useParams<{ section?: string }>();
  const { isAdmin, can } = useAuth();

  const visible = GROUPS.map((group) => ({
    ...group,
    sections: group.sections.filter((item) =>
      item.permission
        ? can(...([] as PermissionCode[]).concat(item.permission))
        : !item.adminOnly || isAdmin,
    ),
  })).filter((group) => group.sections.length > 0);
  const all = visible.flatMap((group) => group.sections);
  const current = all.find((item) => item.key === section);

  if (!current) return <Navigate to={`/configuracoes/${all[0].key}`} replace />;

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900">Configurações</h1>
        <p className="text-sm text-slate-500">
          Dados da empresa, membros, cadastros e Lixeira.
        </p>
      </header>

      <div className="flex gap-6">
        <nav className="w-56 shrink-0 space-y-5" aria-label="Seções das configurações">
          {visible.map((group) => (
            <div key={group.title}>
              <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {group.title}
              </p>
              <div className="space-y-0.5">
                {group.sections.map((item) => (
                  <NavLink
                    key={item.key}
                    to={`/configuracoes/${item.key}`}
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2 text-sm ${
                        isActive
                          ? 'bg-slate-900 font-medium text-white'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="min-w-0 flex-1">{current.render()}</div>
      </div>
    </div>
  );
}
